import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as projectsApi from '../../api/projects';
import { formatDate, formatDateTime } from '../../utils/format';

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Waiting for Client', 'Under Review', 'Completed'];

export default function ProjectDetail() {
  const { id } = useParams();
  const { actor } = useAuth();
  const canAddNotes = actor.role !== 'client';

  const [project, setProject] = useState(null);
  const [progress, setProgress] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = useCallback(() => {
    projectsApi.getProject(id).then((res) => setProject(res.data)).catch((err) => setError(err.response?.data?.message || 'Failed to load project.'));
    projectsApi.getProjectProgress(id).then((res) => setProgress(res.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Admins can always change status; an employee can only if they're on the project's
  // team (the backend enforces this too — this just decides whether to show the control).
  const isOnTeam = (project?.team || []).some((t) => t.id === actor.profile.id);
  const canChangeStatus = actor.role === 'super_admin' || actor.role === 'admin' || (actor.role === 'employee' && isOnTeam);

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (!project || newStatus === project.status) return;
    setError('');
    setUpdatingStatus(true);
    try {
      const res = await projectsApi.updateProjectStatus(id, newStatus);
      setProject(res.data);
      setSuccess(`Status updated to "${newStatus}".`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try {
      await projectsApi.addProjectNote(id, noteText.trim());
      setNoteText('');
      setSuccess('Note added.');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add note.');
    }
  };

  if (!project) {
    return (
      <DashboardLayout title="Project">
        <ErrorBanner message={error} />
        {!error && <Loader text="Loading project..." />}
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Project Detail">
      <div className="page-header">
        <div>
          <h1 className="heading">{project.projectName}</h1>
          <p className="page-subtext">{project.client?.clientName} {project.client?.companyName ? `(${project.client.companyName})` : ''}</p>
        </div>
        <Link to="/projects" className="btn-link">Back to Projects</Link>
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      <div className="card-row">
        <div className="stat-card">
          <div className="stat-label">Status</div>
          {canChangeStatus ? (
            <select value={project.status} onChange={handleStatusChange} disabled={updatingStatus} style={{ marginTop: 4 }}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <div className="stat-value" style={{ fontSize: '13pt' }}><StatusBadge status={project.status} /></div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-label">Priority</div>
          <div className="stat-value" style={{ fontSize: '13pt' }}><StatusBadge status={project.priority} /></div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Due Date</div>
          <div className="stat-value" style={{ fontSize: '13pt' }}>{formatDate(project.dueDate)}</div>
        </div>
        {progress && (
          <div className="stat-card">
            <div className="stat-label">Task Completion</div>
            <div className="stat-value" style={{ fontSize: '13pt' }}>{progress.completedTasks}/{progress.totalTasks} ({progress.percentComplete}%)</div>
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="subheading">Description</h3>
        <p>{project.description || 'No description provided.'}</p>
      </div>

      <div className="card">
        <h3 className="subheading">Team</h3>
        {project.team && project.team.length > 0 ? (
          <p>{project.team.map((t) => t.name).join(', ')}</p>
        ) : (
          <p className="text-muted">No team members assigned yet.</p>
        )}
      </div>

      <div className="card">
        <h3 className="subheading">Tasks</h3>
        {project.tasks && project.tasks.length > 0 ? (
          project.tasks.map((t) => (
            <div key={t.id} className="flex-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
              <Link to="/tasks">{t.title}</Link>
              <div className="btn-group">
                <StatusBadge status={t.priority} />
                <StatusBadge status={t.status} />
                <span className="text-muted">{formatDate(t.dueDate)}</span>
              </div>
            </div>
          ))
        ) : (
          <p className="text-muted">No tasks created for this project yet.</p>
        )}
      </div>

      <div className="card">
        <h3 className="subheading">Notes</h3>
        {project.notes && project.notes.length > 0 ? (
          <div className="comment-list">
            {project.notes.map((n) => (
              <div key={n.id} className="comment-item">
                <div className="comment-meta">{n.author?.name} &middot; {formatDateTime(n.createdAt)}</div>
                <div>{n.note}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">No notes yet.</p>
        )}
        {canAddNotes && (
          <form onSubmit={handleAddNote} style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <input type="text" placeholder="Add a note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            <button type="submit" className="btn btn-small">Add Note</button>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}